import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { X, Lock, Warning, ArrowRight } from "@phosphor-icons/react";

/**
 * Reusable PIN prompt for admin-only destructive actions.
 *
 * Usage:
 *   <PinPromptModal
 *     open={open}
 *     title="Delete ticket?"
 *     description="This permanently removes the repair ticket."
 *     confirmLabel="DELETE TICKET"
 *     destructive
 *     onClose={() => setOpen(false)}
 *     onConfirm={async (pin) => {
 *       await api.delete(`/repair-tickets/${id}`, { headers: { "X-Admin-Pin": pin } });
 *     }}
 *   />
 */
export default function PinPromptModal({
  open,
  title = "Confirm action",
  description = "",
  confirmLabel = "CONFIRM",
  destructive = false,
  // Override the endpoint that decides "is a PIN even configured?" — defaults
  // to the calling user's own PIN status (admin-only flows). Pass
  // "/admin-pin/status" when the action lets a staff user ask an admin to
  // type their PIN (e.g. staff-initiated store switch).
  statusEndpoint = "/users/me/pin-status",
  zIndexClass = "z-[10000]",
  onClose,
  onConfirm,
}) {
  const isStaff = statusEndpoint === "/staff-pin/status";
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasPin, setHasPin] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setSubmitting(false);
    api.get(statusEndpoint)
      .then((r) => setHasPin(!!r.data?.has_pin))
      .catch(() => setHasPin(false));
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [open, statusEndpoint]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin || pin.length < 4) return toast.error("Enter your 4–8 digit PIN");
    setSubmitting(true);
    try {
      await onConfirm(pin);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setPin("");
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${zIndexClass} bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150`}
      onClick={onClose}
      data-testid="pin-modal-overlay"
    >
      <div
        className="bg-white w-full max-w-md rounded-[24px] shadow-2xl shadow-slate-900/20 border border-slate-200/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="pin-modal"
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                destructive
                  ? "bg-gradient-to-br from-[#DC2626] to-[#9B1C1C] shadow-md shadow-red-500/25"
                  : "bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] shadow-md shadow-blue-500/25"
              }`}
            >
              {destructive ? (
                <Warning size={18} weight="fill" className="text-white" />
              ) : (
                <Lock size={18} weight="bold" className="text-white" />
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {isStaff ? "STAFF CONFIRMATION" : "ADMIN AUTHORIZATION"}
              </div>
              <h3 className="font-heading font-bold text-[18px] tracking-tight text-slate-900 leading-tight mt-0.5">
                {title}
              </h3>
              {description && (
                <div className="text-[13px] text-slate-500 mt-1 leading-relaxed">
                  {description}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0"
            data-testid="pin-modal-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {hasPin === false ? (
          <div className="px-6 pb-6">
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
              <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-[12px] text-amber-900 leading-relaxed">
                <strong className="font-semibold">{isStaff ? "No PIN is set for your account." : "No admin PIN is set."}</strong>
                {" "}{isStaff ? "Ask an admin to set one in Settings → Staff & Users." : "Set one in Settings → Staff & Users before using destructive actions."}
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700 transition-colors"
              >
                CLOSE
              </button>
              <Link
                to="/settings"
                onClick={onClose}
                className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[13px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5"
              >
                OPEN SETTINGS <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 pb-6 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">
                {isStaff ? "ENTER YOUR 4-DIGIT PIN" : "ENTER ADMIN PIN"}
              </div>
              <input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={8}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
                className={`w-full h-14 px-4 text-center font-mono font-bold text-3xl tracking-[0.4em] rounded-xl border-2 focus:outline-none transition-all ${
                  destructive
                    ? "border-red-200 focus:border-[#DC2626] focus:ring-4 focus:ring-red-100"
                    : "border-slate-200 focus:border-[#1E5FEB] focus:ring-4 focus:ring-blue-100"
                }`}
                data-testid="pin-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700 transition-colors"
                data-testid="pin-cancel"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={submitting || pin.length < 4}
                className={`flex-1 h-11 rounded-xl disabled:opacity-40 text-white text-[13px] font-semibold transition-all shadow-md ${
                  destructive
                    ? "bg-gradient-to-r from-[#DC2626] to-[#9B1C1C] hover:from-[#9B1C1C] hover:to-[#7F1414] shadow-red-500/25"
                    : "bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] shadow-blue-500/25"
                }`}
                data-testid="pin-confirm"
              >
                {submitting ? "PROCESSING…" : confirmLabel}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
